import React, { useEffect, useState } from "react";
import { connectStore } from "redux-box";
import { module as pictionary } from "@st/pictionary";
import { module as ui } from "@st/ui";
import { id } from "@g/GlobalModal";
import useDebouncing from "@u/useDebouncing";
import GradeApi from "@s/api/grade";
import "../SpeakingFeaturesView.css";

const PAGE_SIZE = 20;

function PictionaryContainer({ pictionary, ui, search = "" }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [grades, setGrades] = useState([]);
  const [activeGrade, setActiveGrade] = useState(null);
  const [currentOffset, setCurrentOffset] = useState(0);

  const debouncedSearch = useDebouncing(search, 400);

  useEffect(() => {
    GradeApi.getAllGrades()
      .then((res) => {
        if (res) setGrades(res);
      })
      .catch((err) => console.error("Error loading grades:", err));
  }, []);

  const gradeTabs = [
    { label: "All", id: null },
    ...grades.map((g) => ({
      label: g.name,
      id: g.id,
    })),
  ];

  useEffect(() => {
    pictionary.requestImages(0, debouncedSearch || "", activeGrade || "");
    setCurrentOffset(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, activeGrade]);

  const handleGradeTab = (gradeId) => {
    setActiveGrade(gradeId);
  };

  const handlePrev = () => {
    const prev = pictionary.pagination ? pictionary.pagination.prev_page : 0;
    pictionary.requestImages(prev, debouncedSearch || "", activeGrade || "");
    setCurrentOffset(prev);
  };

  const handleNext = () => {
    const next = pictionary.pagination ? pictionary.pagination.next_page : 0;
    pictionary.requestImages(next, debouncedSearch || "", activeGrade || "");
    setCurrentOffset(next);
  };

  const openAddModal = () => {
    window.pictionaryEditData = null;
    ui.showModal(id.PICTIONARY_FORM_MODAL);
  };

  const handleEdit = (img) => {
    window.pictionaryEditData = img;
    ui.showModal(id.PICTIONARY_FORM_MODAL);
  };

  const handleDelete = (imageId) => {
    if (window.confirm("Delete this image?")) {
      pictionary.deleteImage(imageId);
    }
  };

  const playAudio = (url) => {
    if (window.sfActiveAudio) {
      window.sfActiveAudio.pause();
    }
    const audio = new Audio(url);
    audio.play().catch((err) => console.error("Audio playback failed:", err));
    window.sfActiveAudio = audio;
  };

  const pagination = pictionary.pagination;
  const hasPrev = pagination && currentOffset > 0;
  const hasNext = pagination && pagination.has_next;
  const totalCount = pagination ? pagination.count : 0;
  const dynamicPageSize = (pagination && pagination.page_size) || 25;
  const pageStart = totalCount === 0 ? 0 : currentOffset * dynamicPageSize + 1;
  const pageEnd = totalCount === 0 ? 0 : pageStart + (pictionary.images ? pictionary.images.length : 0) - 1;

  return (
    <div className="sf-inner">
      {/* ── Grade filter tabs ── */}
      <div className="sf-grade-tabs">
        {gradeTabs.map((tab) => (
          <button
            key={tab.label}
            className={`sf-grade-tab${activeGrade === tab.id ? " sf-grade-tab--active" : ""}`}
            onClick={() => handleGradeTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Table (only this scrolls) ── */}
      {pictionary.isLoading ? (
        <div className="sf-loading">Loading…</div>
      ) : (
        <div className="sf-table-wrapper">
          <table className="sf-table">
            <thead>
              <tr>
                <th>Word</th>
                <th>Preview</th>
                <th>Voice Over</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pictionary.images.length === 0 ? (
                <tr>
                  <td colSpan="4" className="sf-empty">No images found.</td>
                </tr>
              ) : (
                pictionary.images.map((img) => {
                  const imageUrl = img.image_url;
                  return (
                    <tr key={img.id}>
                      <td>
                        <strong>{img.word_id ? img.word_id.word : "--"}</strong>
                      </td>
                      <td>
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt="preview"
                            className="sf-thumb"
                            onClick={() => setPreviewUrl(imageUrl)}
                            title="Click to enlarge"
                          />
                        ) : (
                          <span style={{ color: "#bbb", fontSize: 12 }}>No image</span>
                        )}
                      </td>
                      <td>
                        {img.word_id && img.word_id.voice_over ? (
                          <button
                            className="sf-audio-btn"
                            onClick={() => playAudio(img.word_id.voice_over)}
                          >
                            ▶ Play
                          </button>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <button className="sf-action-btn" onClick={() => handleEdit(img)}>
                          Edit
                        </button>
                        <button
                          className="sf-action-btn sf-action-btn--delete"
                          onClick={() => handleDelete(img.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {!pictionary.isLoading && totalCount > 0 && (
        <div className="sf-pagination">
          <span className="sf-pagination__info">
            {pageStart}–{pageEnd} of {totalCount}
          </span>
          <button className="sf-pagination__btn" onClick={handlePrev} disabled={!hasPrev}>
            ← Prev
          </button>
          <button className="sf-pagination__btn" onClick={handleNext} disabled={!hasNext}>
            Next →
          </button>
        </div>
      )}

      {/* ── FAB ── */}
      <button
        id="pict-add-fab"
        className="sf-fab"
        title="Add Pictionary Image"
        onClick={openAddModal}
      >
        +
      </button>

      {/* ── Image lightbox overlay ── */}
      {previewUrl && (
        <div className="sf-lightbox" onClick={() => setPreviewUrl(null)}>
          <div className="sf-lightbox__box" onClick={(e) => e.stopPropagation()}>
            <button className="sf-lightbox__close" onClick={() => setPreviewUrl(null)}>✕</button>
            <img src={previewUrl} alt="full preview" className="sf-lightbox__img" />
          </div>
        </div>
      )}
    </div>
  );
}

export default connectStore({ pictionary, ui })(PictionaryContainer);
